<template>
  <div>
    <div class="d-flex align-center">
      <v-text-field
        v-model="search"
        placeholder="Search by ID, invoice #, customer, or purpose..."
        prepend-inner-icon="mdi-magnify"
        clearable
        @input="onSearchInput"
      ></v-text-field>
      <v-select
        v-model="sortBy"
        :items="sortOptions"
        label="Sort by"
        class="ml-4"
        style="max-width: 180px"
        dense
        hide-details
        @change="onSortChange"
      ></v-select>
      <v-btn
        icon
        class="ml-2"
        :title="sortDesc ? 'Descending' : 'Ascending'"
        @click="toggleSortDir"
      >
        <v-icon>{{
          sortDesc ? "mdi-sort-descending" : "mdi-sort-ascending"
        }}</v-icon>
      </v-btn>
      <v-menu
        v-model="monthMenu"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        min-width="auto"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            v-model="monthFilter"
            label="Paid in month"
            prepend-inner-icon="mdi-calendar-month"
            readonly
            clearable
            dense
            hide-details
            outlined
            class="ml-4"
            style="max-width: 190px"
            v-bind="attrs"
            v-on="on"
            @click:clear="onMonthChange('')"
          ></v-text-field>
        </template>
        <v-date-picker
          v-model="monthFilter"
          type="month"
          no-title
          @change="onMonthFromPicker"
        ></v-date-picker>
      </v-menu>
      <v-menu
        v-model="weekMenu"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        min-width="auto"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            :value="weekDisplay"
            label="Paid in week"
            prepend-inner-icon="mdi-calendar-week"
            readonly
            clearable
            dense
            hide-details
            outlined
            class="ml-2"
            style="max-width: 230px"
            v-bind="attrs"
            v-on="on"
            @click:clear="onWeekChange('')"
          ></v-text-field>
        </template>
        <v-date-picker
          v-model="weekFilter"
          no-title
          @change="onWeekFromPicker"
        ></v-date-picker>
      </v-menu>
    </div>
    <div class="d-flex align-center mt-2">
      <v-chip small label color="primary" outlined>
        Total: {{ pagination.total || 0 }}
      </v-chip>
      <span v-if="weekFilter" class="ml-2 caption grey--text">
        paid invoices in {{ weekLabel }}
      </span>
      <span v-else-if="monthFilter" class="ml-2 caption grey--text">
        paid invoices in {{ monthLabel }}
      </span>
    </div>
    <v-data-table
      :items="paid_invoices"
      :headers="headers"
      :loading="loading"
      :server-items-length="pagination.total"
      :options.sync="tableOptions"
      :footer-props="{ 'items-per-page-options': [15, 30, 50] }"
      @update:options="onOptionsChange"
    >
      <template v-slot:item.customer.customer_name="{ item }">
        <span
          :class="
            item.customer && item.customer.is_government == 1
              ? 'blue--text'
              : ''
          "
          >{{ item.customer ? item.customer.customer_name : "N/A" }}</span
        >
      </template>
      <template v-slot:item.date_paid="{ item }">
        <span>{{ item.date_paid ? formatDate(item.date_paid) : "—" }}</span>
      </template>
      <template v-slot:item.balance="{ item }">
        <span>{{ item.balance ? item.balance.toLocaleString() : 0 }}</span>
      </template>
      <template v-slot:item.total_amount="{ item }">
        <span>{{
          item.total_amount ? item.total_amount.toLocaleString() : 0
        }}</span>
      </template>
      <template v-slot:item.chequeAmount="{ item }">
        <span v-if="item.customer && item.customer.is_government">{{
          getSixPercent(item.total_amount)
        }}</span>
        <span v-else>n/a</span>
      </template>
      <template v-slot:item.action="{ item }">
        <div class="d-flex">
          <v-btn
            icon
            @click="enablePayablesModal(item)"
            color="primary"
            :loading="detail_loading"
            ><v-icon>mdi-logout-variant</v-icon>
          </v-btn>

          <v-btn
            @click="printModal(item)"
            small
            icon
            color="success"
            class="mt-1"
            :loading="detail_loading"
            ><v-icon>mdi-printer</v-icon></v-btn
          >

          <v-btn
            v-if="can('invoices.delete_paid')"
            icon
            @click="del_invoice(item)"
            color="red"
            class="mr-1"
            :loading="loading"
            ><v-icon class="mr-2">mdi-delete</v-icon></v-btn
          >
        </div>
      </template>
      <template v-slot:item.invoice_status="{ item }">
        <span
          :class="item.invoice_status == 'Paid' ? 'green--text' : 'red--text'"
          >{{ item.invoice_status }}</span
        >
      </template>
      <template v-slot:item.warranty_policy.name="{ item }">
        <v-chip
          v-if="item.warranty_policy"
          small
          :color="item.warranty_policy.color || 'blue'"
          class="white--text"
          >{{ item.warranty_policy.name }}</v-chip
        >
        <span v-else class="grey--text">N/A</span>
      </template>
      <template v-slot:item.warranty_remaining="{ item }">
        <span
          v-if="item.warranty_policy"
          :class="getWarrantyRemainingClass(item)"
        >
          {{ getWarrantyRemaining(item) }}
        </span>
        <span v-else class="grey--text">N/A</span>
      </template>
    </v-data-table>
    <p class="caption grey--text text-right">paidInvoicesTable.vue</p>

    <!-- Lazy loaded -->
    <payables-modal
      v-if="enable_payables_modal"
      :invoice="selected_invoice"
      @close="enable_payables_modal = false"
    />
    <print-modal-card
      v-if="show_print_modal"
      :item="selected_invoice"
      @close="show_print_modal = false"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { invoiceSortOptions } from "@/utils/invoiceSearchFilter";

export default {
  components: {
    payablesModal: () => import("@/components/Payment/payablesModal.vue"),
    PrintModalCard: () => import("./printModalCard.vue"),
  },
  data() {
    return {
      enable_payables_modal: false,
      selected_invoice: [],
      loading: false,
      detail_loading: false,
      headers: [
        { text: "Action", value: "action", width: "80", sortable: false },
        { text: "ID", value: "id" },
        { text: "Invoice #", value: "invoice_code" },
        {
          text: "Account Name",
          value: "customer.customer_name",
          sortable: false,
        },
        { text: "Invoice Date", value: "invoice_date" },
        { text: "Date Paid", value: "date_paid", sortable: false },

        { text: "Total Amount", value: "total_amount" },

        { text: "Purpose", value: "purpose" },
        {
          text: "Warranty Policy",
          value: "warranty_policy.name",
          sortable: false,
        },
        {
          text: "Warranty Remaining",
          value: "warranty_remaining",
          sortable: false,
        },
      ],
      show_print_modal: false,
      search: "",
      searchTimeout: null,
      sortBy: "id",
      sortDesc: true,
      sortOptions: invoiceSortOptions,
      tableOptions: { page: 1, itemsPerPage: 15 },
      initialLoad: true,
      monthFilter: "",
      monthMenu: false,
      weekFilter: "",
      weekMenu: false,
    };
  },
  methods: {
    // Hat-based permission gate; accounts without hats get no access.
    can(key) {
      return this.hasPermission(key);
    },
    getWarrantyExpiryDays(invoiceDate, value, unit) {
      const date = new Date(invoiceDate);
      if (isNaN(date.getTime())) return null;
      if (unit === "months") {
        date.setMonth(date.getMonth() + value);
      } else if (unit === "days") {
        date.setDate(date.getDate() + value);
      } else if (unit === "years") {
        date.setFullYear(date.getFullYear() + value);
      }
      const now = new Date();
      const diffMs = date.getTime() - now.getTime();
      return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    },
    getWarrantyRemaining(item) {
      if (!item.warranty_policy) return "N/A";
      const wp = item.warranty_policy;
      const entries = [
        {
          label: "Major Parts",
          days: this.getWarrantyExpiryDays(
            item.invoice_date,
            wp.major_parts_warranty_value,
            wp.major_parts_warranty_unit
          ),
        },
        {
          label: "Minor Parts",
          days: this.getWarrantyExpiryDays(
            item.invoice_date,
            wp.minor_parts_warranty_value,
            wp.minor_parts_warranty_unit
          ),
        },
        {
          label: "Service",
          days: this.getWarrantyExpiryDays(
            item.invoice_date,
            wp.service_warranty_value,
            wp.service_warranty_unit
          ),
        },
      ];
      const maxEntry = entries.reduce((a, b) => (a.days > b.days ? a : b));
      if (maxEntry.days === null) return "N/A";
      if (maxEntry.days <= 0) return "Expired";
      return maxEntry.days + " day" + (maxEntry.days !== 1 ? "s" : "");
    },
    getWarrantyRemainingClass(item) {
      if (!item.warranty_policy) return "grey--text";
      const wp = item.warranty_policy;
      const days = [
        this.getWarrantyExpiryDays(
          item.invoice_date,
          wp.major_parts_warranty_value,
          wp.major_parts_warranty_unit
        ),
        this.getWarrantyExpiryDays(
          item.invoice_date,
          wp.minor_parts_warranty_value,
          wp.minor_parts_warranty_unit
        ),
        this.getWarrantyExpiryDays(
          item.invoice_date,
          wp.service_warranty_value,
          wp.service_warranty_unit
        ),
      ];
      const maxDays = Math.max(...days.filter((d) => d !== null));
      if (maxDays <= 0) return "red--text";
      if (maxDays <= 30) return "orange--text";
      return "green--text";
    },
    formatDate(value) {
      const date = new Date(value);
      if (isNaN(date.getTime())) return value;
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
    getSixPercent(totalAmount) {
      var vatableAmount = totalAmount / 1.12;
      var vatAmount = totalAmount - vatableAmount;
      var sixPercent = vatAmount / 2;
      var checkAmount = totalAmount - sixPercent;
      var rounded = Math.round(checkAmount);
      var formatted = rounded.toLocaleString(rounded);
      return formatted;
    },
    async enablePayablesModal(invoice) {
      this.detail_loading = true;
      try {
        const detail = await this.get_paid_invoice_detail(invoice.id);
        this.selected_invoice = detail;
        this.enable_payables_modal = true;
      } catch (e) {
        console.error(e);
      }
      this.detail_loading = false;
    },
    ...mapActions({
      get_paid_invoices: "invoice/get_paid_invoices",
      get_paid_invoice_detail: "invoice/get_paid_invoice_detail",
      delete_invoice: "invoice/delete_invoice",
    }),
    async fetchPaidInvoices() {
      this.loading = true;
      await this.get_paid_invoices({
        page: this.tableOptions.page,
        per_page: this.tableOptions.itemsPerPage,
        search: this.search || "",
        sort_by: this.sortBy,
        sort_dir: this.sortDesc ? "desc" : "asc",
        month: this.monthFilter || "",
        week: this.weekFilter || "",
      });
      this.loading = false;
    },
    onSearchInput() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.tableOptions.page = 1;
        this.fetchPaidInvoices();
      }, 400);
    },
    onSortChange() {
      this.tableOptions.page = 1;
      this.fetchPaidInvoices();
    },
    onMonthFromPicker(value) {
      this.monthMenu = false;
      this.onMonthChange(value);
    },
    onMonthChange(value) {
      this.monthFilter = value || "";
      // Month and week filters are mutually exclusive.
      if (this.monthFilter) this.weekFilter = "";
      this.tableOptions.page = 1;
      this.fetchPaidInvoices();
    },
    onWeekFromPicker(value) {
      this.weekMenu = false;
      this.onWeekChange(value);
    },
    onWeekChange(value) {
      this.weekFilter = value || "";
      // Month and week filters are mutually exclusive.
      if (this.weekFilter) this.monthFilter = "";
      this.tableOptions.page = 1;
      this.fetchPaidInvoices();
    },
    toggleSortDir() {
      this.sortDesc = !this.sortDesc;
      this.onSortChange();
    },
    onOptionsChange(options) {
      if (this.initialLoad) {
        this.initialLoad = false;
        return;
      }
      // Header-click sorting feeds the same server-side sort params.
      if (options.sortBy && options.sortBy.length) {
        this.sortBy = options.sortBy[0];
        this.sortDesc = options.sortDesc[0];
      }
      this.tableOptions = options;
      this.fetchPaidInvoices();
    },
    async del_invoice(item) {
      this.loading = true;
      let y = await this.$confirm({
        title: "Delete Invoice",
        message: "Are you sure you want to delete this invoice?",
        color: "red",
        icon: "mdi-delete",
      });
      if (y) {
        await this.delete_invoice(item);
        this.fetchPaidInvoices();
        this.$alert({
          title: "Success",
          message: "Successfully deleted an invoice",
          color: "success",
          icon: "mdi-check-circle",
        });
      } else {
        this.loading = false;
      }
    },
    async printModal(item) {
      this.detail_loading = true;
      try {
        const detail = await this.get_paid_invoice_detail(item.id);
        this.selected_invoice = detail;
        this.show_print_modal = true;
      } catch (e) {
        console.error(e);
      }
      this.detail_loading = false;
    },
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
      paid_invoices: "invoice/paid_invoices",
      pagination: "invoice/paid_invoices_pagination",
      default_server_url: "auth/default_server_url",
      hasPermission: "permission/hasPermission",
      user_permissions: "permission/user_permissions",
    }),
    monthLabel() {
      if (!this.monthFilter) return "";
      const date = new Date(this.monthFilter + "-01");
      if (isNaN(date.getTime())) return this.monthFilter;
      return date.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });
    },
    // Mon-Sun week range containing the selected date.
    weekRange() {
      if (!this.weekFilter) return null;
      const d = new Date(this.weekFilter + "T00:00:00");
      if (isNaN(d.getTime())) return null;
      const day = d.getDay(); // 0=Sun..6=Sat
      const diffToMonday = day === 0 ? -6 : 1 - day;
      const start = new Date(d);
      start.setDate(d.getDate() + diffToMonday);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return { start, end };
    },
    weekDisplay() {
      const r = this.weekRange;
      if (!r) return "";
      const fmt = (dt) =>
        dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      return `${fmt(r.start)} – ${fmt(r.end)}`;
    },
    weekLabel() {
      const r = this.weekRange;
      if (!r) return "";
      const fmt = (dt) =>
        dt.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      return `the week of ${fmt(r.start)} – ${fmt(r.end)}`;
    },
  },
  created() {
    this.fetchPaidInvoices();
  },
};
</script>

<style lang="scss" scoped></style>
